// src/components/EditListingModal.js
import React, { useState, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { updateListing, deleteListing, uploadImages } from '../services/api';

/**
 * Upload multiple files to backend, 
 * returning array with { filename, relativePath }
 * 
 * @Deprecated: use the uploadImages() from api.js instead
 */
// async function uploadImages(imageFiles) {
//   const formData = new FormData();
//   imageFiles.forEach(file => {
//     formData.append('images', file);
//   });

//   const response = await fetch('https://eurocentrumv1.onrender.com/uploads', {
//     method: 'POST', 
//     body: formData
//   });

//   if (!response.ok) {
//     const data = await response.json();
//     throw new Error(data.error || 'Failed to upload images');
//   }

//   return response.json();
// }

function EditListingModal({ listingToEdit, onClose, onRefresh }) {
  const [name, setName] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [customTags, setCustomTags] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');     
  const [location, setLocation] = useState(''); 
  // 1) New integer fields (typed as string in input)
  const [rozloha, setRozloha] = useState('');
  const [pocetIzieb, setPocetIzieb] = useState('');

  const [images, setImages] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Available tags
  const availableTags = ['neplatiči', 'auto', 'byt', 'dom', 'pozemok', 'nebytový priestor', 'priemyselná budova', 'prenájom', 'predaj', 'predané', 'prenajaté', 'aktuálne'];
  const categoryTags = ['neplatiči', 'auto', 'byt', 'dom', 'pozemok', 'nebytový priestor', 'priemyselná budova'];
  // Define sale type tags
  const saleTypeTags = ['predaj', 'prenájom', 'predané', 'prenajaté'];

  const listingId = listingToEdit?._id;

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Populate fields when listingToEdit changes
  useEffect(() => {
    if (listingToEdit) {
      setName(listingToEdit.name || '');
      setSelectedTags(listingToEdit.tags || []);
      // Set custom tags if they exist, or empty string if not
      setCustomTags(listingToEdit.customTags ? listingToEdit.customTags.join(', ') : '');
      setDescription(listingToEdit.description || '');
      setPrice(listingToEdit.price || '');        
      setLocation(listingToEdit.location || '');
      
      // If listingToEdit has numeric fields for rozloha / pocetIzieb
      // Convert them to string
      setRozloha(listingToEdit.rozloha ? String(listingToEdit.rozloha) : '');
      setPocetIzieb(listingToEdit.pocetIzieb ? String(listingToEdit.pocetIzieb) : '');

      const existingImages = (listingToEdit.images || []).map((img) => ({
        ...img,
        isNew: false,
      }));
      setImages(existingImages);
      setNewFiles([]);
      setCurrentIndex(0);
    }
  }, [listingToEdit]);

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      images.forEach(img => {
        if (img.tempUrl && img.relativePath) {
          URL.revokeObjectURL(img.relativePath);
        }
      });
    };
  }, [images]);

  // Handle tag selection
  const handleTagToggle = (tag) => {
    setError('');
    
    if (categoryTags.includes(tag)) {
      // If selecting a category tag, remove other category tags
      const newTags = selectedTags.filter(t => !categoryTags.includes(t));
      setSelectedTags([...newTags, tag]);
    } else {
      // For non-category tags, just toggle them
      if (selectedTags.includes(tag)) {
        setSelectedTags(selectedTags.filter(t => t !== tag));
      } else {
        setSelectedTags([...selectedTags, tag]);
      }
    }
  };

  // DROPZONE
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    onDrop: (acceptedFiles) => {
      // Create preview URLs and store files
      const newImageObjs = acceptedFiles.map((file) => ({
        filename: file.name,
        relativePath: URL.createObjectURL(file),
        isNew: true,
        tempUrl: true // Mark as temporary URL for cleanup
      }));
      
      // Add to existing files
      setNewFiles((prev) => [...prev, ...acceptedFiles]);
      setImages((prev) => [...prev, ...newImageObjs]);
      
      // Set current index to the first new image
      if (newImageObjs.length > 0) {
        setCurrentIndex(images.length);
      }
      
      // For debugging
      console.log("Added images:", newImageObjs);
      console.log("Current images array:", [...images, ...newImageObjs]);
    },
  });

  // PREV/NEXT IMAGE
  const handlePrev = () => {
    if (images.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };
  const handleNext = () => {
    if (images.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  // REMOVE CURRENT IMAGE
  const removeCurrentImage = () => {
    if (images.length === 0) return;

    const updated = [...images];
    const removed = images[currentIndex];
    updated.splice(currentIndex, 1);
    setImages(updated);

    // If newly added, remove from newFiles as well
    if (removed?.isNew) {
      setNewFiles((prev) => prev.filter((file) => file.name !== removed.filename));
    }

    if (updated.length === 0) {
      setCurrentIndex(0);
    } else {
      setCurrentIndex((prev) => Math.min(prev, updated.length - 1));
    }
  };

  // SUBMIT / SAVE CHANGES
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate that at least one category tag is selected
    const hasCategoryTag = selectedTags.some(tag => categoryTags.includes(tag));
    
    if (!hasCategoryTag) {
      setError('Prosím vyberte aspoň jednu kategóriu (auto, byt, dom, pozemok, nebytový priestor, priemyselná budova alebo neplatiči)');
      setIsSubmitting(false);
      return;
    }

    // Check if predaj or prenájom is selected when property type tags are selected
    const hasPropertyTag = selectedTags.some(tag => ['auto', 'byt', 'dom', 'pozemok', 'nebytový priestor', 'priemyselná budova'].includes(tag));
    const hasSaleTypeTag = selectedTags.some(tag => saleTypeTags.includes(tag));
    
    if (hasPropertyTag && !hasSaleTypeTag) {
      setError('Prosím vyberte typ predaja (na predaj, na nájom, predané alebo prenajaté)');
      setIsSubmitting(false);
      return;
    }

    if (!listingId) {
      console.error('No valid listing ID was provided!');
      return;
    }

    try {
      console.log("Starting update for listing:", listingId);
      
      // Upload any new images
      let uploadedImages = [];
      
      // Filter out images that need to be uploaded
      const newImageObjs = images.filter((img) => img.isNew);
      console.log("Images that need to be uploaded:", newImageObjs.length);
      
      const filesToUpload = newImageObjs
        .map((img) => newFiles.find((f) => f.name === img.filename))
        .filter(Boolean);
      
      console.log("Files to upload:", filesToUpload.length);
      
      // Upload the new files if there are any
      if (filesToUpload.length > 0) {
        console.log(`Uploading ${filesToUpload.length} new images to Google Cloud Storage...`);
        
        try {
          const uploadResult = await uploadImages(filesToUpload);
          console.log("Upload result:", uploadResult);
          
          if (!uploadResult.files || uploadResult.files.length === 0) {
            throw new Error('No files were uploaded successfully');
          }
          
          console.log('Successfully uploaded images:', uploadResult.files);
          uploadedImages = uploadResult.files;
        } catch (uploadError) {
          console.error("Error uploading images:", uploadError);
          throw new Error(`Failed to upload images: ${uploadError.message}`);
        }
      }

      // Build final images array - keep existing images and add new uploaded ones
      // First, filter out any existing images that weren't removed
      const existingImages = images.filter(img => !img.isNew).map(img => ({
        filename: img.filename,
        relativePath: img.relativePath,
        fileUrl: img.fileUrl,
        gcsBlobName: img.gcsBlobName
      }));
      
      // Combine existing images with newly uploaded ones
      const finalImages = [...existingImages, ...uploadedImages];
      
      console.log("Final images array for saving:", finalImages);

      // Check if neplatiči is selected
      const isNeplatiSelected = selectedTags.includes('neplatiči');

      // Convert numeric fields from strings => numbers
      const parsedRozloha = rozloha ? Number(rozloha) : 0;
      const parsedPocetIzieb = pocetIzieb ? Number(pocetIzieb) : 0;

      // Parse custom tags into array
      const customTagsArray = customTags
        .split(/[,\n]/) // Split by comma OR newline
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      // Construct data with all fields
      const data = {
        name,
        tags: selectedTags,
        customTags: customTagsArray,
        description,
        price: isNeplatiSelected ? '' : price,
        location: isNeplatiSelected ? '' : location,
        rozloha: parsedRozloha,
        pocetIzieb: parsedPocetIzieb,
        images: finalImages,
      };

      // Send to server
      await updateListing(listingId, data);

      // Try to refresh listings, but ensure modal closes even if refresh fails
      try {
        if (onRefresh) {
          await onRefresh();
        }
      } catch (refreshError) {
        console.error('Failed to refresh listings after update:', refreshError);
        // Continue execution to close modal
      }
      
      // Always close the modal after successful update
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Failed to update listing with images:', err);
      setError(err.message || 'Nastala chyba pri ukladaní inzerátu');
    } finally {
      setIsSubmitting(false);
    }
  };

  // DELETE LISTING
  const handleDelete = async () => {
    if (!listingId) {
      console.error('No valid listing ID was provided!');
      return;
    }

    try {
      setIsDeleting(true);
      await deleteListing(listingId);
      
      // Try to refresh listings, but ensure modal closes even if refresh fails
      try {
        if (onRefresh) {
          await onRefresh();
        }
      } catch (refreshError) {
        console.error('Failed to refresh listings after delete:', refreshError);
        // Continue execution to close modal
      }
      
      // Always close the modal after successful deletion
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Failed to delete listing:', err);
      setError('Failed to delete listing: ' + (err.message || 'Unknown error'));
    } finally {
      setIsDeleting(false);
    }
  };

  if (!listingToEdit) return null;

  // Stop click from closing modal
  const stopClickPropagation = (e) => e.stopPropagation();

  // Current image in the slider
  const currentImage = images[currentIndex] || null;
  let displaySrc = '';
  
  if (currentImage?.relativePath) {
    console.log("Processing image for display:", currentImage);
    
    if (currentImage.tempUrl || currentImage.relativePath.startsWith('blob:')) {
      console.log("Using blob URL for preview:", currentImage.relativePath);
      displaySrc = currentImage.relativePath;
    } else if (currentImage.fileUrl && currentImage.fileUrl.includes('storage.googleapis.com')) {
      console.log("Using Google Cloud Storage fileUrl for display:", currentImage.fileUrl);
      displaySrc = currentImage.fileUrl;
    } else if (currentImage.gcsBlobName) {
      const gcsUrl = `https://storage.googleapis.com/eurocentrumpd-bucket/${currentImage.gcsBlobName}`;
      console.log("Using Google Cloud Storage gcsBlobName for display:", gcsUrl);
      displaySrc = gcsUrl;
    } else if (currentImage.filename) {
      const gcsUrl = `https://storage.googleapis.com/eurocentrumpd-bucket/${currentImage.filename}`;
      console.log("Using Google Cloud Storage filename for display:", gcsUrl);
      displaySrc = gcsUrl;
    } else if (currentImage.relativePath.startsWith('http')) {
      console.log("Using http URL for display:", currentImage.relativePath);
      displaySrc = currentImage.relativePath;
    }
  } else if (currentImage) {
    console.log("No relativePath for image:", currentImage);
  }
  
  // Render the image slider
  const renderImageSlider = () => {
    return (
      <div style={styles.imageWrapper}>
        {currentImage ? (
          <>
            <img
              src={displaySrc}
              alt={currentImage.filename || "Listing image"}
              style={styles.previewImage}
              onError={(e) => {
                console.error("Failed to load image:", displaySrc);
                e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22400%22%20viewBox%3D%220%200%20600%20400%22%20preserveAspectRatio%3D%22none%22%3E%3Cfilter%20id%3D%22b%22%3E%3CfeGaussianBlur%20stdDeviation%3D%2240%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22600%22%20height%3D%22400%22%20fill%3D%22%23777%22%2F%3E%3Cg%20filter%3D%22url%28%23b%29%22%3E%3Cg%20fill-opacity%3D%22.5%22%3E%3Ccircle%20fill%3D%22%23999%22%20cx%3D%22300%22%20cy%3D%22200%22%20r%3D%22120%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3Ctext%20x%3D%22300%22%20y%3D%22210%22%20text-anchor%3D%22middle%22%20font-family%3D%22Arial%2C%20sans-serif%22%20font-size%3D%2224%22%20font-weight%3D%22bold%22%20fill%3D%22%23fff%22%3EImage Not Found%3C%2Ftext%3E%3C%2Fsvg%3E';
              }}
            />
            <div style={styles.arrowContainer}>
              <button
                type="button"
                style={styles.arrowButton}
                onClick={handlePrev}
                className="round-button nav-arrow"
                aria-label="Previous image"
              >
                &#10094;
              </button>
              <button
                type="button"
                style={styles.arrowButton}
                onClick={handleNext}
                className="round-button nav-arrow"
                aria-label="Next image"
              >
                &#10095;
              </button>
            </div>
            <button
              type="button"
              onClick={removeCurrentImage}
              style={styles.removeButton}
              className="round-button"
              aria-label="Remove image"
            >
              &#10005;
            </button>
          </>
        ) : (
          <div style={styles.noImagesText}>No images uploaded yet</div>
        )}
      </div>
    );
  };

  // Check if neplatiči is selected
  const isNeplatiSelected = selectedTags.includes('neplatiči');

  return (
    <>
      {/* Adding styles */}
      <style>{`
        .modern-button {
          background-color: rgb(44, 105, 160);
          color: #fff;
          border: none;
          padding: 8px 14px;
          border-radius: 5px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .modern-button:hover {
          background-color: rgb(36, 85, 130);
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        .modern-button:focus {
          outline: 2px solid rgb(36, 85, 130);
        }

        .danger-button {
          background-color: #dc3545;
          color: #fff;
          border: none;
          padding: 8px 14px;
          border-radius: 5px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .danger-button:hover {
          background-color: #bb2d3b;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }

        .round-button {
          background-color: rgba(0,0,0,0.5);
          color: #fff;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }
        .round-button:hover {
          background-color: rgba(0,0,0,0.7);
          transform: scale(1.05);
        }

        .close-button {
          position: absolute;
          top: '15px',
          right: '15px',
          background: 'rgba(0, 0, 0, 0.1)',
          border: 'none',
          borderRadius: '50%',
          width: '30px',
          height: '30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: '#333',
          fontSize: '16px',
          zIndex: 10,
        },
        .close-button:hover {
          background-color: rgba(0,0,0,0.7);
          transform: scale(1.1);
        }

        .dropzone:hover {
          background-color: #f0f8ff;
          border-color: rgb(44, 105, 160);
        }

        .active-tag {
          background-color: rgb(44, 105, 160);
          color: white;
          border-color: rgb(44, 105, 160);
        }

        .tag-button {
          padding: 6px 12px;
          margin: 4px;
          border-radius: 16px;
          cursor: pointer;
          border: 1px solid #ccc;
          background-color: #f8f9fa;
          transition: all 0.2s;
        }
        
        .tag-button.selected {
          background-color: rgb(44, 105, 160);
          color: white;
          border-color: rgb(44, 105, 160);
        }
        
        .tag-button.neplatiči.selected {
          background-color: #dc3545;
          border-color: #dc3545;
        }
        
        .tag-button[data-tag="aktuálne"].selected,
        .tag-button[data-tag="aktualne"].selected {
          background-color: #28a745; /* Green color for aktuálne */
          color: white;
          border-color: #28a745;
          font-weight: bold;
        }
        
        .tag-button[data-tag="predané"].selected {
          background-color: #cc0000;
          color: white;
          border-color: #cc0000;
          font-weight: bold;
        }
        
        .tags-container {
          display: flex;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }

        .delete-confirm {
          position: absolute;
          bottom: 20px;
          left: 20px;
          background-color: #f8d7da;
          border: 1px solid #f5c2c7;
          padding: 10px 15px;
          border-radius: 5px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
      `}</style>

      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={stopClickPropagation}>
          <h2 style={styles.modalTitle}>Upraviť inzerát</h2>
          
          {error && <div style={styles.error}>{error}</div>}
          
          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Left side: image slider */}
            <div style={styles.leftSide}>
              {renderImageSlider()}
            </div>

            {/* Right side: dropzone + fields */}
            <div style={styles.rightSide}>
              <div
                {...getRootProps({ className: 'dropzone', style: styles.dropzone })}
              >
                <input {...getInputProps()} />
                <p style={styles.dropText}>Kliknite a nahrajte obrázky</p>
              </div>

              {/* Name */}
              <label style={styles.label}>{isNeplatiSelected ? 'Meno' : 'Názov'}</label>
              <input
                style={styles.underlineInput}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              {/* Tags Buttons */}
              <label style={styles.label}>Tagy</label>
              <div className="tags-container">
                {availableTags
                  .filter(tag => {
                    // If neplatiči is selected, hide predaj, prenájom, and aktuálne tags
                    if (selectedTags.includes('neplatiči')) {
                      return !['predaj', 'prenájom', 'aktuálne'].includes(tag);
                    }
                    return true;
                  })
                  .map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => handleTagToggle(tag)}
                      className={`tag-button ${tag === 'neplatiči' ? 'neplatiči' : ''} ${selectedTags.includes(tag) ? 'selected' : ''}`}
                      data-tag={tag}
                    >
                      {tag === 'prenájom' ? 'na nájom' : 
                       tag === 'predaj' ? 'na predaj' : tag}
                    </button>
                  ))}
              </div>

              {/* Custom Tags */}
              <div style={styles.formGroup}>
                <label style={styles.labelBlock}>Vlastné značky (oddelené čiarkou alebo novým riadkom)</label>
                <textarea
                  value={customTags}
                  onChange={(e) => setCustomTags(e.target.value)}
                  placeholder="napr. novostavba, zariadený, garáž"
                  style={{...styles.underlineTextarea, minHeight: '60px'}}
                  rows={3}
                />
                <div style={styles.tagInfo}>
                  Vlastné značky sa zobrazia s šedým pozadím.
                </div>
              </div>

              {/* Description */}
              <label style={styles.label}>Popis</label>
              <textarea
                style={styles.underlineTextarea}
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />

              {/* Location - only shown if neplatiči not selected */}
              {!isNeplatiSelected && (
                <>
                  <label style={styles.label}>Poloha</label>
                  <input
                    style={styles.underlineInput}
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                </>
              )}

              {/* Price - only shown if neplatiči not selected */}
              {!isNeplatiSelected && (
                <>
                  <label style={styles.label}>Cena</label>
                  <input
                    style={styles.underlineInput}
                    type="text"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </>
              )}

              {/* Rozloha - hide if auto tag is selected or neplatici is selected */}
              {!selectedTags.includes('auto') && !isNeplatiSelected && (
                <>
                  <label style={styles.label}>Rozloha (m²)</label>
                  <input
                    style={styles.underlineInput}
                    type="text"
                    value={rozloha}
                    onChange={(e) => setRozloha(e.target.value)}
                  />
                </>
              )}

              {/* Pocet izieb - hide if auto tag is selected or neplatici is selected */}
              {!selectedTags.includes('auto') && !isNeplatiSelected && (
                <>
                  <label style={styles.label}>Druh nehnuteľnosti</label>
                  <input
                    style={styles.underlineInput}
                    type="text"
                    value={pocetIzieb}
                    onChange={(e) => setPocetIzieb(e.target.value)}
                  />
                </>
              )}

              {/* Buttons */}
              <div style={styles.buttonRow}>
                {!showDeleteConfirm ? (
                  <button
                    type="button"
                    className="danger-button"
                    style={{ marginRight: 'auto' }}
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isDeleting}
                  >
                    Vymazať
                  </button>
                ) : (
                  <div className="delete-confirm">
                    <span>Naozaj chcete vymazať tento príspevok?</span>
                    <button 
                      type="button"
                      className="danger-button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Vymazávam...' : 'Áno, vymazať'}
                    </button>
                    <button 
                      type="button"
                      className="modern-button"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                    >
                      Zrušiť
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="modern-button"
                  style={{ marginRight: '1rem' }}
                >
                  Zrušiť
                </button>
                <button
                  type="submit"
                  className="modern-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Ukladám...' : 'Uložiť'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default EditListingModal;

// Minimal styling
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    width: '900px',
    maxWidth: '95%',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
    position: 'relative',
  },
  modalTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1.8rem',
    fontWeight: '600',
    color: '#333',
  },
  form: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',
  },
  leftSide: {
    flex: 1,
    border: 'none',
    borderRadius: '6px',
    padding: '1rem',
    marginBottom: '1rem',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '350px',
    boxShadow: 'none',
  },
  imageWrapper: {
    position: 'relative',
    textAlign: 'center',
    width: '320px',
    height: '320px',
    margin: '0 auto',
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: '6px',
  },
  pathText: {
    marginTop: '0.5rem',
    fontSize: '0.85rem',
    color: '#333',
    wordWrap: 'break-word',
    maxWidth: '320px',
    textAlign: 'center',
  },
  removeButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    background: 'rgba(255, 255, 255, 0.7)',
    border: 'none',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#333',
    fontSize: '16px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    zIndex: 10,
  },
  arrowContainer: {
    position: 'absolute',
    bottom: '8px',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 1rem',
    zIndex: 10,
  },
  arrowButton: {
    background: 'rgba(255, 255, 255, 0.7)',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#333',
    fontSize: '18px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  noImagesText: {
    color: '#888',
    fontStyle: 'italic',
  },
  rightSide: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    border: 'none',
  },
  dropzone: {
    border: 'none',
    borderBottom: '2px solid rgb(44, 105, 160)',
    borderRadius: 0,
    padding: '1rem',
    textAlign: 'center',
    marginBottom: '1rem',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
  },
  dropText: {
    margin: 0,
    color: '#666',
    fontSize: '0.95rem',
  },
  label: {
    fontWeight: '400',
    marginBottom: '0.2rem',
    color: '#000',
    fontSize: '0.9rem',
  },
  underlineInput: {
    marginBottom: '1rem',
    padding: '0.3rem 0',
    border: 'none',
    borderRadius: 0,
    borderBottom: '2px solid rgb(44, 105, 160)',
    fontSize: '1rem',
    color: '#333',
    backgroundColor: 'transparent',
    outline: 'none',
    width: '100%',
  },
  underlineTextarea: {
    marginBottom: '1rem',
    padding: '0.3rem 0',
    border: 'none',
    borderRadius: 0,
    borderBottom: '2px solid rgb(44, 105, 160)',
    fontSize: '1rem',
    color: '#333',
    backgroundColor: 'transparent',
    outline: 'none',
    resize: 'vertical',
    whiteSpace: 'pre-wrap',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 'auto',
  },
  error: {
    color: 'red',
    marginBottom: '1rem',
    textAlign: 'center',
    padding: '0.5rem',
    backgroundColor: 'rgba(255, 0, 0, 0.1)',
    borderRadius: '4px',
  },
  tagInfo: {
    fontSize: '0.8rem',
    color: '#666',
    marginTop: '0.3rem',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  labelBlock: {
    display: 'block',
    marginBottom: '0.2rem',
    fontWeight: '400',
    color: '#000',
    fontSize: '0.9rem',
  },
  input: {
    marginBottom: '0.5rem',
    padding: '0.3rem 0',
    border: 'none',
    borderRadius: 0,
    borderBottom: '2px solid rgb(44, 105, 160)',
    fontSize: '1rem',
    color: '#333',
    backgroundColor: 'transparent',
    outline: 'none',
  },
};
// Test comment
